import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ChevronLeft, BookMarked, Star, BookOpen, Bookmark } from "lucide-react";
import { AppShell } from "@/components/app-shell";
import {
  DCard,
  EmptyState,
  HeroCard,
  LoadError,
  Pill,
  ProgressBar,
  SectionHeader,
} from "@/components/dryvn";
import { AddPanel, LifeField, LifeInput, LifeSelect, SubmitButton } from "@/components/life-forms";
import { CollapsibleList, ListSearch, useListFilter } from "@/components/collapsible-list";
// Screen migration (2026-07-05): reading log via hook.
// 2026-07-15: web-entry form + honest empty/error states.
import { type Reading } from "@/data/mock";
import { useAddReading, useReading } from "@/lib/queries";

export const Route = createFileRoute("/more/reading")({
  head: () => ({
    meta: [
      { title: "Reading log — Dryvn Life" },
      { name: "description", content: "Currently reading, finished, and the to-read shelf." },
    ],
  }),
  component: ReadingPage,
});

const currently = (r: Reading) => r.status === "reading";
const finished = (r: Reading) => r.status === "finished";
const want = (r: Reading) => r.status === "want";

// View status → backend enum (adapt.ts maps the reverse on read).
const WIRE_STATUS = {
  want: "want_to_read",
  reading: "reading",
  finished: "finished",
} as const;

function StarRow({ rating }: { rating: number }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className="h-3 w-3"
          fill={n <= rating ? "var(--dryvn-sun)" : "transparent"}
          style={{ color: n <= rating ? "var(--dryvn-sun)" : "var(--dryvn-line)" }}
        />
      ))}
    </div>
  );
}

function ReadingPage() {
  const { data: reading, isError, error } = useReading();
  const addReading = useAddReading();

  const [items, setItems] = useState<Reading[]>(reading);
  useEffect(() => setItems(reading), [reading]);

  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [status, setStatus] = useState<Reading["status"]>("reading");
  const [submitError, setSubmitError] = useState(false);

  const submit = (close: () => void) => {
    const t = title.trim();
    if (!t) return;
    // Optimistic insert for instant feedback; snapshot so a failed save can
    // roll it back instead of leaving a book that never persisted.
    const snapshot = items;
    setItems((prev) => [
      { id: crypto.randomUUID(), title: t, author: author.trim(), status },
      ...prev,
    ]);
    setSubmitError(false);
    addReading.mutate(
      { title: t, author: author.trim() || undefined, status: WIRE_STATUS[status] },
      {
        onSuccess: () => {
          setTitle("");
          setAuthor("");
          setStatus("reading");
          close();
        },
        onError: () => {
          setItems(snapshot);
          setSubmitError(true);
        },
      },
    );
  };

  const reads = items.filter(currently);
  const done = items.filter(finished);
  const finishedThisYear = done.length;
  const avgRating =
    done.reduce((s, r) => s + (r.rating ?? 0), 0) / (done.filter((r) => r.rating).length || 1);

  // One search across the whole shelf — the three sections are one library
  // split by status, so splitting the search three ways would only make the
  // reader guess which box to type in. The hero counts stay on the full set.
  const {
    query: bookQuery,
    setQuery: setBookQuery,
    results: bookMatches,
    active: bookSearchActive,
  } = useListFilter(items, (r) => [r.title, r.author]);
  const shownReads = bookMatches.filter(currently);
  const shownDone = bookMatches.filter(finished);
  const shownWant = bookMatches.filter(want);

  return (
    <AppShell>
      <Link
        to="/more"
        className="mb-4 inline-flex items-center gap-1 text-sm"
        style={{ color: "var(--dryvn-primary)" }}
      >
        <ChevronLeft className="h-4 w-4" /> More
      </Link>

      <HeroCard
        eyebrow="Reading"
        title={
          <>
            Books &{" "}
            <em className="italic" style={{ color: "var(--dryvn-primary-deep)" }}>
              ideas
            </em>
            .
          </>
        }
      >
        <div className="flex flex-wrap gap-2 text-xs">
          <Pill tone="life">{reads.length} in progress</Pill>
          <Pill tone="primary">{finishedThisYear} finished this year</Pill>
          {avgRating > 0 && <Pill tone="amber">Avg ★ {avgRating.toFixed(1)}</Pill>}
        </div>
      </HeroCard>

      {/* Web entry — a second way in besides SMS. */}
      <div className="mt-6">
        <AddPanel label="Add a book">
          {(close) => (
            <form
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                submit(close);
              }}
            >
              <LifeField label="Title">
                <LifeInput
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Book title"
                />
              </LifeField>
              <div className="grid grid-cols-2 gap-3">
                <LifeField label="Author">
                  <LifeInput
                    value={author}
                    onChange={(e) => setAuthor(e.target.value)}
                    placeholder="Optional"
                  />
                </LifeField>
                <LifeField label="Shelf">
                  <LifeSelect
                    value={status}
                    onChange={(e) => setStatus(e.target.value as Reading["status"])}
                  >
                    <option value="reading">Currently reading</option>
                    <option value="want">Want to read</option>
                    <option value="finished">Finished</option>
                  </LifeSelect>
                </LifeField>
              </div>
              <SubmitButton disabled={!title.trim()}>Add book</SubmitButton>
              {submitError && (
                <p className="text-xs" style={{ color: "var(--dryvn-warm-deep)" }}>
                  We couldn't save that just now — give it another try in a moment.
                </p>
              )}
            </form>
          )}
        </AddPanel>
      </div>

      {isError && (
        <div className="mt-6">
          <LoadError error={error} />
        </div>
      )}

      {!isError && items.length === 0 && (
        <div className="mt-6">
          <EmptyState
            title="Your shelf is empty. Add a book above, or text your Dryvn number as you read."
            smsExamples={["reading Project Hail Mary", "finished James — 5 stars, gutting"]}
          />
        </div>
      )}

      {items.length > 0 && (
        <div className="mt-8">
          <ListSearch
            value={bookQuery}
            onChange={setBookQuery}
            placeholder="Search the shelf…"
            label="Search books by title or author"
            className="max-w-sm"
          />
          {bookSearchActive && bookMatches.length === 0 && (
            <DCard>
              <p className="text-sm" style={{ color: "var(--dryvn-grey)" }}>
                No books match that search.
              </p>
            </DCard>
          )}
        </div>
      )}

      {/* Currently reading — feature grid */}
      {shownReads.length > 0 && (
        <div className="mt-8">
          <SectionHeader eyebrow="Right now" title="Currently reading" />
          <CollapsibleList items={shownReads} resource="books" filtered={bookSearchActive}>
            {(shown) => (
              <div className="grid gap-4 sm:grid-cols-2">
                {shown.map((r) => (
                  <DCard key={r.id} className="p-0 overflow-hidden">
                    <div
                      className="flex h-32 items-end p-4"
                      style={{ backgroundImage: "var(--grad-hero)" }}
                    >
                      <BookOpen className="h-8 w-8 text-white/85 drop-shadow" />
                    </div>
                    <div className="p-4">
                      <p
                        className="font-display text-lg leading-tight"
                        style={{ color: "var(--dryvn-ink)" }}
                      >
                        {r.title}
                      </p>
                      <p className="text-xs" style={{ color: "var(--dryvn-grey)" }}>
                        {r.author}
                      </p>
                      {r.progress !== undefined && (
                        <div className="mt-3">
                          <div
                            className="mb-1 flex items-center justify-between text-[11px]"
                            style={{ color: "var(--dryvn-grey)" }}
                          >
                            <span>Progress</span>
                            <span className="font-mono">{r.progress}%</span>
                          </div>
                          <ProgressBar value={r.progress} max={100} tint="primary" />
                        </div>
                      )}
                      {r.note && (
                        <p
                          className="mt-3 rounded-lg border-l-2 px-3 py-2 text-[13px] italic leading-snug"
                          style={{
                            borderColor: "var(--dryvn-life)",
                            backgroundColor:
                              "color-mix(in oklab, var(--dryvn-life) 12%, transparent)",
                            color: "var(--dryvn-ink)",
                          }}
                        >
                          “{r.note}”
                        </p>
                      )}
                    </div>
                  </DCard>
                ))}
              </div>
            )}
          </CollapsibleList>
        </div>
      )}

      {/* Finished shelf */}
      {shownDone.length > 0 && (
        <div className="mt-8">
          <SectionHeader eyebrow="This year" title="Finished shelf" />
          <CollapsibleList items={shownDone} resource="books" filtered={bookSearchActive}>
            {(shown) => (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {shown.map((r) => (
                  <DCard key={r.id}>
                    <div className="flex items-start gap-3">
                      <span
                        className="grid h-10 w-10 shrink-0 place-items-center rounded-lg"
                        style={{ backgroundImage: "var(--grad-lime)" }}
                      >
                        <BookMarked className="h-5 w-5 text-white" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium" style={{ color: "var(--dryvn-ink)" }}>
                          {r.title}
                        </p>
                        <p className="truncate text-xs" style={{ color: "var(--dryvn-grey)" }}>
                          {r.author}
                        </p>
                        <div className="mt-1.5 flex items-center gap-2">
                          {r.rating && <StarRow rating={r.rating} />}
                          {r.finishedOn && (
                            <span
                              className="font-mono text-[10px]"
                              style={{ color: "var(--dryvn-grey)" }}
                            >
                              {r.finishedOn}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {r.note && (
                      <p
                        className="mt-3 text-[12px] italic leading-snug"
                        style={{ color: "var(--dryvn-grey)" }}
                      >
                        “{r.note}”
                      </p>
                    )}
                  </DCard>
                ))}
              </div>
            )}
          </CollapsibleList>
        </div>
      )}

      {/* Want to read */}
      {shownWant.length > 0 && (
        <div className="mt-8">
          <SectionHeader eyebrow="Later" title="To read" />
          <CollapsibleList items={shownWant} resource="books" filtered={bookSearchActive}>
            {(shown) => (
              <DCard>
                <ul className="grid gap-2 sm:grid-cols-2">
                  {shown.map((r) => (
                    <li key={r.id} className="flex items-center gap-2 text-sm">
                      <Bookmark
                        className="h-3.5 w-3.5"
                        style={{ color: "var(--dryvn-warm-deep)" }}
                      />
                      <span style={{ color: "var(--dryvn-ink)" }}>{r.title}</span>
                      <span className="text-xs" style={{ color: "var(--dryvn-grey)" }}>
                        — {r.author}
                      </span>
                    </li>
                  ))}
                </ul>
              </DCard>
            )}
          </CollapsibleList>
        </div>
      )}
    </AppShell>
  );
}
